import React from 'react';
import PropTypes from 'prop-types';
import {
  View,
  ImageBackground,
  TouchableOpacity,
  SectionList,
} from 'react-native';
import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import analytics from '../../../../modules/analytics';

import ServiceItem from '../ServiceItem';
import { LinkTo } from '../../../../hooks/useLinkTo';
import ThemedButton from '../../../ui/Button';
import {
  BodyText,
  Button,
  Stack,
  Absolute,
  Touchable,
} from '../../../ui';

import GlyphIcon, { CHEVRON_UP, CHEVRON_DOWN } from '../../../ui/GlyphIcon';
import ConditionalWrapper from '../../../ui/ConditionalWrapper';

import './ServiceList.module.scss';
import { HighlightedServicesSwiper } from '../HighlightedServicesSwiper';
import { ServiceListModal } from './ServiceListModal';
import { ServiceCategoryPills, ServiceCategoryPillsProps } from './ServiceCategoryPills';
import SSErrorBoundary from '../../../SSErrorBoundary';
import { ServiceSectionListProps } from './types';
import { ProviderService } from '../../../../api/Providers/Services';
import { ServiceSection } from '../HighlightedServicesSwiper/types';
import { ServiceSectionHeader } from './ServiceSectionHeader';
import { DEFAULT_GROUP_NAME } from './constants';
import { ServiceListBreadCrumbs } from './ServiceListBreadCrumbs';
import { PublicProvider } from '../../../../api/Providers';
import { ClientProviderProfile } from '../../../../api/ProfileClientView';

const LOADING_SPINNER = require('../../../../../images/ajax-loader.gif');

const DEFAULT_VISIBLE_PROMOTIONS = 3;

/**
 * A component used to separate individual service items in the list
 */
const VisibleSeparator = () => <View styleName="separator" />;
const HiddenSeparator = () => <View style={{ width: '100%', height: 4 }} />;

/**
 * The footer component for service groups. This supplies a "show all" button for the promotions
 * group
 */
const ServiceSectionFooter = ({
  sectionIndex,
  onClick,
  showAllPromotions,
  allPromotionsCount,
}) => {
  // 0 is promotions
  if (sectionIndex === 0 && allPromotionsCount > DEFAULT_VISIBLE_PROMOTIONS) {
    const text = `See All Promotions (${allPromotionsCount})`;
    let glyphContent = CHEVRON_DOWN;
    if (showAllPromotions) {
      glyphContent = CHEVRON_UP;
    }

    return (
      <View styleName="promotionFooter">
        <ThemedButton
          appearance="text-primary"
          onClick={onClick}
          size="small"
          styleName="allPromotionToggle"
        >
          {text}
          <GlyphIcon
            styleName="glyphIcon"
            onPress={onClick}
            content={glyphContent}
          />
        </ThemedButton>
      </View>
    );
  }

  return null;
};

ServiceSectionFooter.propTypes = {
  /** The section to render, supplied by `SectionList` */
  sectionIndex: PropTypes.number.isRequired,
  /** True when the show all button is clicked, otherwise false */
  onClick: PropTypes.func,
  /** True when all promotions are being displayed, otherwise false */
  showAllPromotions: PropTypes.bool,
  /** The total number of all active promotions */
  allPromotionsCount: PropTypes.number,
};

ServiceSectionFooter.defaultProps = {
  onClick: null,
  showAllPromotions: false,
  allPromotionsCount: 0,
};

/**
 * Footer to display at the very end of the list. This renders the loading spinner when loading the
 * next page of services for a profile
 */
const ListFooterComponent = ({ morePagesToLoad, renderRight }) => (
  <View>
    {renderRight ? <VisibleSeparator /> : null}
    {morePagesToLoad && (
      <View styleName="loadingSpinnerWrapper">
        <View>
          <ImageBackground
            styleName="loadingSpinner"
            source={LOADING_SPINNER}
          >
            <View styleName="loadingSpinnerBorder" />
          </ImageBackground>
        </View>
      </View>
    )}
  </View>
);

ListFooterComponent.propTypes = {
  /** True if there are more pages to load, otherwise false */
  morePagesToLoad: PropTypes.bool,
  /** Truthy if we are on a standalone list page */
  renderRight: PropTypes.any,
};

ListFooterComponent.defaultProps = {
  morePagesToLoad: false,
  renderRight: null,
};

type ServiceListProps = {
  appearance: 'default' | 'notifyMe';
  /** True if the current user is not logged in */
  isAnon?: boolean;
  /** True if we are able to edit services */
  isEditMode?: boolean;
  /** True if a pro is viewing their own profile */
  isOwnProfile?: boolean;
  /** True if booking should be disabled */
  disableBooking?: boolean;
  /** The provider for which to render services, if any */
  provider: PublicProvider | ClientProviderProfile | null;
  /** The service groups to display */
  serviceGroups: Array<ServiceSection>;
  /** Props to supply to individual service items. Either a hash to supply to all of them or a
   * function which is given the service item and should return a hash. */
  serviceItemProps: Record<string, any> | ((service: ProviderService) => Record<string, any>);
  /** Function called when end of list is reached */
  onEndReached: () => void;
  /** True if there are more pages to load, otherwise false */
  morePagesToLoad?: boolean;
  /** Function called when toggling of promotions is requested */
  onPromotionsToggle: () => void;
  /** The number of active promotions (displayed in a summary) */
  promotionCount: number;
  /** True to show all promotions, otherwise false */
  showAllPromotions?: boolean;
  /** Optional click handler for the item row; hides the action button */
  onItemPress: (service: ProviderService) => void;
  /** Function that returns a component to display next to the ServiceItem */
  renderRight: () => React.ReactNode;
  /** The ID of the matched service from a search card */
  highlightedServiceId?: number;
  /** Test for sifting service and category to the top of service list */
  isInSortedServicesTest?: boolean;
  /** maximum number of services to show */
  maxServiceCount?: number | null;
  /** callback to disable maxServiceCount */
  onShowAllServices?: () => void;
  /** True if we're showing recommendations at the bottom and will truncate the service list */
  hasProviderRecommendationData?: boolean;
  /** Test for putting see all services in its own overlay */
  isInSeeAllServicesTest?: boolean;
  /** Current route name for behavior for 'see times' */
  currentRouteName: string,
  linkTo: LinkTo,
  popularServiceRankingMap: object,
  isMobile: boolean;
};

type ServiceListState = {
  isModalOpen?: boolean;
  activeTabIndex: number;
  serviceGroupsShortened: Array<ServiceSection>;
};

/**
 * Renders a list of each service offered by a given provider, with promoted services
 * listed at the top. Services are grouped by group name.
 */
class ServiceList extends React.Component<ServiceListProps, ServiceListState> {
  constructor(props) {
    super(props);
    this.state = {
      isModalOpen: false,
      activeTabIndex: -1,
      serviceGroupsShortened: [],
    };
  }

  static defaultProps: Partial<ServiceListProps> = {
    isAnon: true,
    isEditMode: false,
    isOwnProfile: false,
    disableBooking: false,
    morePagesToLoad: true,
    showAllPromotions: false,
    highlightedServiceId: undefined,
    isInSortedServicesTest: false,
    isInSeeAllServicesTest: false,
    onShowAllServices: () => { },
  };

  static getDerivedStateFromProps(
    props: ServiceListProps,
    state: ServiceListState,
  ): ServiceListState {
    const {
      isInSeeAllServicesTest,
      serviceGroups,
      highlightedServiceId,
    } = props;
    const {
      activeTabIndex,
    } = state;
    let result: ServiceListState = null;
    let highlightedTabIndex = 0;

    const serviceGroupsShortened = [];
    if (isInSeeAllServicesTest) { // ATL-3806
      serviceGroups.forEach((serviceGroup, groupIndex) => {
        const cloneArray = serviceGroup.data.slice();

        if (highlightedServiceId && serviceGroup.name !== 'Popular Services') {
          const highlightedServiceIndex = (
            cloneArray.findIndex(svc => svc.id === highlightedServiceId)
          );

          if (highlightedServiceIndex !== -1) {
            const [highlightedService] = cloneArray.splice(highlightedServiceIndex, 1);
            cloneArray.unshift(highlightedService);
            highlightedTabIndex = groupIndex;
          }
        }

        serviceGroupsShortened.push({
          index: serviceGroup.index,
          name: serviceGroup.name || DEFAULT_GROUP_NAME,
          startingOverallIndex: serviceGroup.startingOverallIndex,
          data: cloneArray,
        });
      });

      result = {
        ...state,
        serviceGroupsShortened,
      };

      if (serviceGroupsShortened.length > 0 && activeTabIndex === -1) {
        result.activeTabIndex = highlightedTabIndex;
      }
    }

    return result;
  }

  /**
   * Toggles the `showAllPromotions` state variable
   */
  handleAllPromotionsToggle = () => {
    const { onPromotionsToggle } = this.props;

    analytics.track('client_proprofile_allpromotions_click');

    if (typeof onPromotionsToggle === 'function') {
      onPromotionsToggle();
    }
  };

  /**
   * Renders a single list item, used by `SectionList`
   */
  renderItem = ({
    service,
    index,
    section,
    renderRight,
  }) => {
    const {
      isEditMode,
      isOwnProfile,
      disableBooking,
      provider,
      onItemPress,
      appearance,
      highlightedServiceId,
      popularServiceRankingMap,
    } = this.props;
    const profile = provider;
    let { serviceItemProps } = this.props;
    let providerPhone;

    if (provider) {
      providerPhone = provider.public_phone_number;
    }

    if (typeof serviceItemProps === 'function') {
      serviceItemProps = serviceItemProps(service);
    }

    // data for SeeRatings test
    const seeReviewsProps = {
      provider,
      service,
    };

    const allowOnlineBooking = provider
      && provider.is_effectively_online_bookable
      && !provider.is_disabled;

    return (
      <ConditionalWrapper
        condition={onItemPress !== null}
        wrapper={children => (
          <TouchableOpacity onPress={() => onItemPress(service)}>{children}</TouchableOpacity>
        )}
      >
        <Box paddingX={8}>
          <View styleName={renderRight ? 'serviceItemRow' : 'serviceItemWrapper'}>
            <ServiceItem
              service={service}
              providerId={profile.id}
              isEditMode={isEditMode}
              isOwnProfile={isOwnProfile}
              isDisabled={disableBooking || !!onItemPress}
              allowOnlineBooking={allowOnlineBooking}
              providerSmartPricingEnabled={provider && provider.smart_pricing_enabled}
              group={section}
              index={index}
              providerPhone={providerPhone}
              seeReviewsProps={seeReviewsProps}
              appearance={appearance}
              renderRight={renderRight}
              highlightedServiceId={highlightedServiceId}
              popularServiceRankingMap={popularServiceRankingMap}
              {...serviceItemProps}
            />
          </View>
        </Box>
      </ConditionalWrapper>
    );
  };

  renderSectionHeader = ({ section: { name } }) => (
    !name
      ? <HiddenSeparator />
      : (
        <ServiceSectionHeader>
          <BodyText
            color={colors.blackInBlack}
            weight="thin"
          >
            {name}
          </BodyText>
        </ServiceSectionHeader>
      )
  );

  renderListHeader = () => {
    const {
      provider,
      serviceGroups,
      isAnon,
      isOwnProfile,
      highlightedServiceId,
      isInSortedServicesTest,
      isInSeeAllServicesTest,
    } = this.props;
    if (isOwnProfile || isInSeeAllServicesTest) {
      return null;
    }

    return (
      <React.Fragment>
        <SSErrorBoundary>
          <HighlightedServicesSwiper
            provider={provider}
            isLoggedIn={!isAnon}
            serviceGroups={serviceGroups}
            highlightedServiceId={highlightedServiceId}
            isInSortedServicesTest={isInSortedServicesTest}
          />
        </SSErrorBoundary>
      </React.Fragment>
    );
  };

  renderListFooter = () => {
    const {
      morePagesToLoad,
      renderRight,
    } = this.props;

    return (
      <ListFooterComponent
        morePagesToLoad={morePagesToLoad}
        renderRight={renderRight}
      />
    );
  };

  /**
   * Renders a section footer, used by `SectionList`
   */
  renderSectionFooter = props => {
    const { showAllPromotions, promotionCount } = this.props;

    if (!props.section.data.length) {
      return null;
    }

    return (
      <ServiceSectionFooter
        showAllPromotions={showAllPromotions}
        onClick={this.handleAllPromotionsToggle}
        allPromotionsCount={promotionCount}
        sectionIndex={props.sectionIndex || props.section.index}
        {...props}
      />
    );
  };

  handleTabChange = (newTabIndex: number) => {
    this.setState({
      activeTabIndex: newTabIndex,
    });
  };

  handleSeeAllServicesClick = () => {
    const {
      serviceGroupsShortened,
      activeTabIndex,
    } = this.state;

    analytics.track(
      'client_seeallservices_clicked_seeallservicesbutton', {
        category_tab_name: (
          serviceGroupsShortened[activeTabIndex].name
        ),
      },
    );
    this.setState({ isModalOpen: true });
  };

  handleModalRequestClose = () => {
    analytics.track(
      'client_seeallservices_modal_closed',
    );
    this.setState({ isModalOpen: false });
  };

  computeServiceGroups = (serviceGroups: ServiceSection[]): {
    wasTruncated: boolean;
    groups: ServiceSection[];
  } => {
    const {
      maxServiceCount,
      hasProviderRecommendationData,
      isMobile,
    } = this.props;
    const isServicelistTruncated = maxServiceCount && hasProviderRecommendationData && isMobile;

    if (isServicelistTruncated) {
      let serviceCount: number = 0;

      const filteredGroups = serviceGroups.map(group => ({
        ...group,
        data: group.data.filter(() => {
          if (serviceCount < maxServiceCount) {
            serviceCount += 1;
            return true;
          }

          return false;
        }),
      })).filter(group => group.data.length);

      if (serviceCount < maxServiceCount) {
        return {
          wasTruncated: false,
          groups: filteredGroups,
        };
      }

      return {
        wasTruncated: true,
        groups: filteredGroups,
      };
    }

    return {
      wasTruncated: false,
      groups: serviceGroups,
    };
  };

  render() {
    const {
      appearance,
      renderRight,
      onEndReached,
      serviceGroups,
      currentRouteName,
      isInSeeAllServicesTest,
      onShowAllServices,
      provider,
    } = this.props;
    const {
      activeTabIndex,
      serviceGroupsShortened,
      isModalOpen,
    } = this.state;

    const {
      groups,
      wasTruncated,
    } = this.computeServiceGroups(serviceGroups);
    const showSimplifiedList = appearance === 'notifyMe';
    const lastSection = groups.length ? groups[groups.length - 1] : null;

    let initialNumToRender = 0;

    if (lastSection) {
      // calculate the total number of items, including section headers / footers, to render
      // `SectionList` is optimized for very long lists of things, but we don't have to worry about
      // that just yet, so we just render everything we have.
      initialNumToRender = (
        // total number of promotion / service items
        lastSection.startingOverallIndex
        + lastSection.data.length
        // and also the number of section headers / footers
        + groups.length * 2
      );
    }
    const sectionListProps: ServiceSectionListProps = {
      ListHeaderComponent: showSimplifiedList ? null : this.renderListHeader,
      ListFooterComponent: this.renderListFooter,
      ItemSeparatorComponent: renderRight ? VisibleSeparator : HiddenSeparator,
      sections: groups,
      renderSectionHeader: isInSeeAllServicesTest
        ? null
        : this.renderSectionHeader as unknown as ServiceSectionListProps['renderSectionHeader'],
      renderSectionFooter: this.renderSectionFooter,
      renderItem: entry => this.renderItem({
        section: entry.section,
        index: entry.index,
        service: entry.item,
        renderRight,
      }),
      initialNumToRender,
      onEndReached,
      disableVirtualization: true,
      keyExtractor: (item, idx) => `${item.id}-${item.name}-${idx}`,
    };
    if (!isInSeeAllServicesTest) {
      return (
        <Stack testID="serviceList" paddingBottom={12}>
          <SectionList
            {...sectionListProps}
          />
          {!wasTruncated && <ServiceListBreadCrumbs provider={provider} />}
          {wasTruncated && (
            <Touchable onClick={onShowAllServices}>
              <Absolute
                right={0}
                left={0}
                bottom={-72}
                paddingBottom={72}
              >
                <Box
                  style={{
                    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.9), #fff 95%)',
                  }}
                  minHeight={100}
                  justifyContent="flex-end"
                  alignItems="center"
                  paddingBottom={16}
                >
                  <Box
                    borderStyle="solid"
                    borderBottomWidth={1}
                    borderBottomColor={colors.blackInBlack}
                  >
                    <BodyText testID="service-list__see-all">
                      View All Services
                    </BodyText>
                  </Box>
                </Box>
              </Absolute>
            </Touchable>
          )}
        </Stack>
      );
    }

    return (
      <Stack paddingBottom={12}>
        {
          isModalOpen && !currentRouteName.includes('book') && (
            <ServiceListModal
              onRequestClose={this.handleModalRequestClose}
              initialTabIndex={activeTabIndex}
              {...sectionListProps}
            />
          )
        }

        {serviceGroups.length > 1 && (
          <ServiceCategoryPills
            serviceGroups={serviceGroups as unknown as ServiceCategoryPillsProps['serviceGroups']}
            onTabChange={this.handleTabChange}
            activeTabIndex={activeTabIndex}
          />
        )}
        <Box testID="serviceList">
          <SectionList
            {...sectionListProps}
            sections={activeTabIndex !== -1 ? [serviceGroupsShortened[activeTabIndex]] : []}
          />
        </Box>

        {(serviceGroupsShortened.length > 1) && (
          <Inline justifyContent="center" padding={12}>
            <Button
              appearance="text-light"
              onClick={this.handleSeeAllServicesClick}
            >
              See All Services
            </Button>
          </Inline>
        )}
      </Stack>
    );
  }
}

export default ServiceList;
